import gql from "graphql-tag";
import { defineRule } from "vee-validate";
import { mimes as mimesRule } from "@vee-validate/rules";

defineRule("emailDomainList", (value) => {
  if (value) {
    for (let domain of value) {
      if (
        !/@[a-zA-z0-9%_-]+(?:(\.[a-zA-z0-9%_-]+))*\.[a-zA-z0-9]+/.test(domain)
      ) {
        return '"' + value + '" ist keine valide domain (Format: @example.com)';
      }
    }
  }
  return true;
});

defineRule("isTrue", (value) => {
  return value === true;
});

defineRule("multiCheckboxRequired", (value) => {
  if (Object.entries(value).some(([_, val]) => val === true)) return true;
  return "Wählen Sie mindestens eine der Optionen aus";
});

defineRule("isUniqueEmail", (value, params) => {
  const ownEmail = params?.ownEmail;
  return (async () => {
    if (!value) {
      return true;
    }
    if (value === ownEmail) {
      return true;
    }
    let vueRoot = await window.vueRoot;
    const valid = await vueRoot.$apollo
      .query({
        query: gql`
          query EmailAvailability($email: String!) {
            emailAvailability(email: $email) {
              email
            }
          }
        `,
        variables: {
          email: value,
        },
      })
      .then(({ data }) => {
        return data.emailAvailability.email;
      });
    if (valid) {
      return true;
    }
    return false;
  })();
});

defineRule("dateGreaterThan", (value, params) => {
  const { otherValue, otherValueName } = params || {};
    const dateSelf = new Date(value);
    if (!otherValue) {
      return true;
    }
    const dateOther = new Date(otherValue);
    const result = dateSelf.valueOf() > dateOther.valueOf();
    if (result) {
      return true;
    } else if (otherValueName) {
      return `Das Datum muss nach ${otherValueName} liegen.`;
    } else {
      return false;
    }
});

defineRule("dateLessThan", (value, params) => {
  const { otherValue, otherValueName } = params || {};
    const dateSelf = new Date(value);
    if (
      !otherValue ||
      !value ||
      String(value) === "Invalid date" ||
      (Object.prototype.toString(value) === "[object Date]" && isNaN(value))
    ) {
      return true;
    }
    const dateOther = new Date(otherValue);
    const result = dateSelf.valueOf() < dateOther.valueOf();
    if (result) {
      return true;
    } else if (otherValueName) {
      return `Das Datum muss vor ${otherValueName} liegen.`;
    } else {
      return false;
    }
});

defineRule("lte_other", (value, params) => {
  const { target, target_name } = params || {};
    if (
      value === null ||
      value === undefined ||
      target === null ||
      target === undefined
    ) {
      return true;
    } else if (Number(value) <= Number(target)) {
      return true;
    } else {
      return `Der Start darf nicht nach ${target_name} liegen`;
    }
});

defineRule("gte_other", (value, params) => {
  const { target, target_name } = params || {};
    if (
      value === null ||
      value === undefined ||
      target === null ||
      target === undefined
    ) {
      return true;
    } else if (Number(value) >= Number(target)) {
      return true;
    } else {
      return `Das Ende dar nicht vor ${target_name} liegen`;
    }
});

defineRule("customRegex", (value, params) => {
  const { regex, errorMessage } = params || {};
  let dynamicMessage = errorMessage;
  return (async () => {
    const worker = new Worker(
      new URL("./customRegexWorker.js", import.meta.url)
    );
    const regexPromise = new Promise((resolve) => {
      worker.onmessage = (e) => resolve(e.data);
    });
    worker.postMessage(JSON.stringify({ regex, value }));

    const timeout = new Promise((resolve) => {
      setTimeout(() => {
        dynamicMessage =
          "Validation rule took too long to check. This is likely due to a maliciously constructed regular expression.";
        resolve(false);
      }, 10_000);
    });

    const matches = await Promise.race([regexPromise, timeout]);
    worker.terminate();
    if (matches) {
      return true;
    }
    return dynamicMessage;
  })();
});

defineRule("customCheckboxValidation", (value) => {
  return value === "true" || value === true;
});

function buildMailRegex(domains) {
  const result =
    "(" +
    domains.map((d) => "\\@" + d.replace(".", "\\.") + "$").join("|") +
    ")";
  return new RegExp(result);
}

const blacklist = buildMailRegex([
  "gmail.com",
  "googlemail.com",
  "web.de",
  "outlook.com",
  "gmx.de",
]);

defineRule("allowedEmail", (value) => {
  if (blacklist.test(value)) {
    return "bitte geben sie eine Firmen-E-Mail-Adresse ein. Falls dies eine Firmen-E-Mail-Adresse ist, nehmen sie bitte Kontakt zu unserer Mitgliederverwaltung auf.";
  }
  return true;
});

defineRule("noEmotes", (value) => {
  return !/\p{Extended_Pictographic}/u.test(value);
});

const ALLOWED_DOMAINS = [
  "http://localhost:8080/",
  "http://localhost:8000/",
  "https://dsagnet.de/",
  "https://dsag.de/",
  "https://dsag.kesslerdigital.cloud/",
  "https://dsag-kesslerdigital.kesslerdigital.cloud/",
];
const urlRegex =
  /^(https?)?:\/\/(www\.)?[-a-zA-Z0-9@:%._~#=]{1,256}(\.|:)[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_.~#?&//=]*)$/i;

defineRule("shortLinkValidation", (value) => {
  if (!urlRegex.test(value)) {
    return false;
  }
  let v = value;
  if (!v.startsWith("https://") && !v.startsWith("http://")) {
    v = "https://" + v;
  }
  for (const d of ALLOWED_DOMAINS) {
    if (v.startsWith(d)) return true;
  }
  return false;
});

defineRule("shortCodeValidation", (value) => {
  return /^[a-zA-Z0-9\-_~]+$/.test(value);
});

defineRule("regexMessage", (value, params) => {
  const { regex, errorMessage } = params || {};
    if (regex.test(value)) {
      return true;
    }
  if (typeof errorMessage === "string") {
    return errorMessage;
  }
  return errorMessage(value);
});

defineRule("plz", (value, params) => {
  const otherValue = params?.otherValue;
  if (!value) {
    return true;
  }
  if (otherValue && otherValue.postcode_regex) {
    const reg = new RegExp(otherValue.postcode_regex);
    if (reg.test(value)) {
      return true;
    }
    return "Bitte eine gültige PLZ eingeben";
  }
  if (/^[0-9A-Za-z\s-]{1,20}$/.test(value)) {
    return true;
  }
  return "Bitte eine gültige PLZ eingeben";
});

defineRule("mimes", (value, params, ctx) => {
  const result = mimesRule(value, params, ctx);
  return result === true ? true : "Dateien müssen einen gültigen Dateityp haben";
});
